/**
 * AuthGate - Redirects unauthenticated visitors to sign-in.
 * Owner bypass: set REACT_APP_OWNER_BYPASS_TOKEN in .env, then visit glowlens.net?preview=YOUR_TOKEN
 * once to set a cookie and access the home page without signing in.
 */

import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAWSAuth as useAuth } from '../contexts/AWSAuthContext';
import styled from 'styled-components';
import { Loader } from 'lucide-react';
import { theme } from '../styles/theme';

const BYPASS_COOKIE = 'glowlens_owner_preview';
const LoadingContainer = styled.div`
  min-height: calc(100vh - 72px);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoadingSpinner = styled(Loader)`
  animation: spin 1s linear infinite;
  color: ${theme.colors.primary[500]};
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

function hasOwnerBypass(): boolean {
  try {
    const token = process.env.REACT_APP_OWNER_BYPASS_TOKEN;
    if (!token) return false;
    const params = new URLSearchParams(window.location.search);
    const preview = params.get('preview');
    if (preview && preview === token) {
      document.cookie = `${BYPASS_COOKIE}=1; path=/; max-age=2592000`; // 30 days
      if (window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.delete('preview');
        window.history.replaceState({}, '', url.toString());
      }
      return true;
    }
    return document.cookie.includes(`${BYPASS_COOKIE}=1`);
  } catch {
    return false;
  }
}

interface AuthGateProps {
  children: React.ReactNode;
}

const AuthGate: React.FC<AuthGateProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const preview = params.get('preview');
    const token = process.env.REACT_APP_OWNER_BYPASS_TOKEN;
    if (preview && token && preview === token && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.delete('preview');
      window.history.replaceState({}, '', url.toString());
    }
  }, [location.search]);

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner size={48} />
      </LoadingContainer>
    );
  }

  const hasBypass = hasOwnerBypass();

  if (user || hasBypass) {
    return <>{children}</>;
  }

  return <Navigate to="/signin" state={{ from: location }} replace />;
};

export default AuthGate;
